import { vyDate, type VyDate } from '@vy/utils';

// Parse "MMM YYYY" as a UTC wall-clock date. Without `keepLocalTime: true`,
// dayjs interprets the string in the user's local timezone and then converts
// to UTC by shifting the timestamp — which lands the parsed instant in the
// PREVIOUS month for users east of UTC. The flag preserves the wall-clock
// values (year/month/day) when relabelling the dayjs as UTC, so "Jun 2025"
// always parses to UTC Jun 1.
function parseMonthKeyDayjs(monthKey: string): VyDate {
  return vyDate(monthKey, { format: 'MMM YYYY', keepLocalTime: true });
}

export function parseMonthKey(monthKey: string): Date {
  return parseMonthKeyDayjs(monthKey).startOf('month').toDate();
}

export function monthKeyBounds(monthKey: string): { start: Date; end: Date } {
  const startDayjs = parseMonthKeyDayjs(monthKey).startOf('month');
  return {
    start: startDayjs.toDate(),
    end: startDayjs.endOf('month').toDate(),
  };
}

export function formatMonthLabel(monthKey: string): string {
  const parts = monthKey.split(' ');
  const month = parts[0];
  const year = parts[parts.length - 1];
  return `${month} ${year.slice(-2)}`;
}

export function filterMonthKeysByDateRange(
  monthKeys: string[],
  fromDate: Date | null,
  toDate: Date | null,
): string[] {
  if (!fromDate && !toDate) {
    return monthKeys;
  }

  // Compare everything via `vyDate` so we stay in UTC on both sides of the
  // inequality. Mixing a local-time `new Date()` with a UTC-anchored `toDate`
  // produced an off-by-one month for users east of UTC (chart showed one
  // extra trailing month past the "To" picker value).
  const fromMonth = fromDate ? vyDate(fromDate).startOf('month') : null;
  const toMonth = toDate ? vyDate(toDate).startOf('month') : null;

  return monthKeys.filter((key) => {
    const monthDate = parseMonthKeyDayjs(key).startOf('month');
    if (fromMonth && monthDate.isBefore(fromMonth)) {
      return false;
    }
    if (toMonth && monthDate.isAfter(toMonth)) {
      return false;
    }
    return true;
  });
}
